import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFluxStore } from '@evertrue/et-flux';
import EventsSource from 'apps/events/sources/events-source';
import EventsStore from 'apps/events/stores/events-store';
import IsGated from 'components/is-gated';
import _ from 'underscore';
import { FlexTable } from '@evertrue/sdk';
import { Icon } from '@evertrue/et-components';
import { formatRows } from '../configs/event-table-rows';
import EventsTableConfig from 'apps/events/configs/events-table-config';

const mapEventsStore = () => ({
  page: EventsStore.getPage(),
  eventCount: EventsStore.getEventCount(),
  sortParams: EventsStore.getSortParams(),
});

const EventsTable = ({ data = [], loading }) => {
  const { page, eventCount, sortParams } = useFluxStore(EventsStore, () => mapEventsStore);
  const sortModel = useMemo(() => {
    if (!sortParams?.prop) return [];

    const field =
      sortParams.prop === 'name.untouched' ? 'event' : sortParams.prop === 'start_time' ? 'date' : sortParams.prop;

    return [
      {
        field,
        sort: sortParams.reverse ? 'desc' : 'asc',
      },
    ];
  }, [sortParams]);

  const handleSortModelChange = useCallback((newModel) => {
    if (newModel.length === 0) return;

    const { field, sort } = newModel[0];
    const reverse = sort === 'desc';
    const sortField = field === 'event' ? 'name.untouched' : field === 'date' ? 'start_time' : field;

    EventsSource.newSort(sortField, reverse);
  }, []);

  const rows = useMemo(() => formatRows(data, EventsTableConfig), [data]);

  const [paginationModel, setPaginationModel] = useState({
    page: page - 1 || 0,
    pageSize: 25,
  });

  const handlePaginationModelChange = useCallback((model) => {
    setPaginationModel(model);
    EventsSource.setPage(model.page + 1);
  }, []);

  return (
    <IsGated
      feature="events_eventbrite"
      render={({ show }) => {
        const config = show ? EventsTableConfig : _.reject(EventsTableConfig, { field: 'unmatched_count' });
        return (
          <div style={{ height: 'calc(100vh - 205px)', overflowY: 'auto', width: '100%' }}>
            <FlexTable
              tableKey="events-table"
              columns={config}
              rows={rows}
              loading={loading}
              pageSizeOptions={[25]}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              rowCount={eventCount}
              emptyStateIcon={<Icon icon="search" />}
              emptyStateMessage="Your search didn't return any events"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              getRowHeight={() => 'auto'}
              autosizeOptions={{
                columns: ['event', 'date', 'location', 'matched_count', 'unmatched_count'],
                includeOutliers: true,
                includeHeaders: true,
                expand: true,
              }}
            />
          </div>
        );
      }}
    />
  );
};

EventsTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};

export default EventsTable;
